import { useCallback, useEffect, useState } from 'react';
import {
  Content,
  Header,
  Page,
  Progress,
  Table,
  TableColumn,
} from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DescriptionIcon from '@material-ui/icons/Description';
import RefreshIcon from '@material-ui/icons/Refresh';
import StopIcon from '@material-ui/icons/Stop';

type PipelineRow = {
  app: string;
  repoUrl: string;
  repoBranch: string;
  repoSubdir: string;
  schedule: string;
  hasCredentials: boolean;
  suspend: boolean;
  lastRunStartedAt?: string;
  lastRunPhase?: string;
  lastRunStep?: string;
  lastRunStepMessage?: string;
  lastRunFinishedAt?: string;
};

const RUNNING_PHASES = new Set(['Running', 'Pending']);

// Cores do Material-UI (theme não exposto aqui) pro badge de status - mesmo
// verde/vermelho já usados nas mensagens de sucesso/erro do formulário.
const PHASE_COLOR: Record<string, string> = {
  Succeeded: '#15803d',
  Failed: '#b91c1c',
  Error: '#b91c1c',
  Running: '#a16207',
  Pending: '#a16207',
};

const EMPTY_FORM = {
  app: '',
  repoUrl: '',
  repoBranch: '',
  repoSubdir: '',
  schedule: '*/5 * * * *',
  privateRepo: false,
  gitUsername: 'oauth2',
  gitToken: '',
  runNow: true,
};

export const PipelinesPage = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [pipelines, setPipelines] = useState<PipelineRow[] | undefined>();
  const [activeTab, setActiveTab] = useState(0);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingHasCredentials, setEditingHasCredentials] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [successMessage, setSuccessMessage] = useState<string>();
  const [logDialogApp, setLogDialogApp] = useState<string>();
  const [logDialogLoading, setLogDialogLoading] = useState(false);
  const [logDialogContent, setLogDialogContent] = useState<string>();
  const [logDialogError, setLogDialogError] = useState<string>();
  const [logDialogArchived, setLogDialogArchived] = useState(false);
  const [busyApp, setBusyApp] = useState<string>();
  const [actionError, setActionError] = useState<string>();
  const [actionMessage, setActionMessage] = useState<string>();

  const runAction = async (app: string, action: 'run' | 'cancel') => {
    setBusyApp(app);
    setActionError(undefined);
    setActionMessage(undefined);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('pipeline-registration');
      const response = await fetchApi.fetch(`${baseUrl}/pipelines/${encodeURIComponent(app)}/${action}`, {
        method: 'POST',
      });
      const body = (await response.json().catch(() => ({}))) as {
        error?: string;
        workflowName?: string;
      };
      if (!response.ok) {
        throw new Error(body.error ?? `Falha ao ${action === 'run' ? 'disparar rebuild' : 'cancelar'} (${response.status})`);
      }
      setActionMessage(
        action === 'run'
          ? `Rebuild de '${app}' disparado (${body.workflowName}). Acompanhe em "Último resultado" - atualiza sozinho.`
          : `Execução de '${app}' cancelada.`,
      );
      await loadPipelines();
    } catch (error) {
      setActionError((error as Error).message);
    } finally {
      setBusyApp(undefined);
    }
  };

  const openLog = async (app: string) => {
    setLogDialogApp(app);
    setLogDialogLoading(true);
    setLogDialogContent(undefined);
    setLogDialogError(undefined);
    setLogDialogArchived(false);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('pipeline-registration');
      const response = await fetchApi.fetch(`${baseUrl}/pipelines/${encodeURIComponent(app)}/log`);
      const body = (await response.json().catch(() => ({}))) as {
        log?: string;
        error?: string;
        archived?: boolean;
      };
      if (!response.ok) {
        throw new Error(body.error ?? `Falha ao buscar log (${response.status})`);
      }
      setLogDialogContent(body.log ?? '');
      setLogDialogArchived(!!body.archived);
    } catch (error) {
      setLogDialogError((error as Error).message);
    } finally {
      setLogDialogLoading(false);
    }
  };

  const setField = <K extends keyof typeof EMPTY_FORM>(
    key: K,
    value: (typeof EMPTY_FORM)[K],
  ) => setForm(current => ({ ...current, [key]: value }));

  const loadPipelines = useCallback(async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('pipeline-registration');
      const response = await fetchApi.fetch(`${baseUrl}/pipelines`);
      const body = (await response.json().catch(() => ({}))) as {
        pipelines?: PipelineRow[];
        error?: string;
      };
      if (!response.ok) {
        throw new Error(body.error ?? `Falha ao carregar pipelines (${response.status})`);
      }
      setPipelines(body.pipelines ?? []);
      setErrorMessage(undefined);
    } catch (error) {
      setErrorMessage((error as Error).message);
    }
  }, [discoveryApi, fetchApi]);

  useEffect(() => {
    loadPipelines();
  }, [loadPipelines]);

  // Atualização automática pra acompanhar Rebuild/Cancelar (e o próprio
  // cron) sem precisar de F5 - só na aba de listagem, onde faz sentido.
  useEffect(() => {
    if (activeTab !== 1) return undefined;
    const id = setInterval(loadPipelines, 5000);
    return () => clearInterval(id);
  }, [activeTab, loadPipelines]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingHasCredentials(false);
  };

  const editPipeline = (row: PipelineRow) => {
    setForm({
      app: row.app,
      repoUrl: row.repoUrl,
      repoBranch: row.repoBranch,
      repoSubdir: row.repoSubdir,
      schedule: row.schedule,
      privateRepo: row.hasCredentials,
      gitUsername: 'oauth2',
      gitToken: '',
      runNow: false,
    });
    setEditingHasCredentials(row.hasCredentials);
    setSuccessMessage(undefined);
    setActiveTab(0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const savePipeline = async () => {
    setSaving(true);
    setErrorMessage(undefined);
    setSuccessMessage(undefined);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('pipeline-registration');
      const response = await fetchApi.fetch(`${baseUrl}/pipelines`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const body = (await response.json().catch(() => ({}))) as {
        error?: string;
        workflowName?: string;
      };
      if (!response.ok) {
        throw new Error(body.error ?? `Falha ao cadastrar pipeline (${response.status})`);
      }
      setSuccessMessage(
        body.workflowName
          ? `Pipeline salvo e execução ${body.workflowName} iniciada no Argo.`
          : 'Pipeline salvo. A próxima execução seguirá o agendamento informado.',
      );
      resetForm();
      await loadPipelines();
    } catch (error) {
      setErrorMessage((error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const columns: TableColumn<PipelineRow>[] = [
    { title: 'Aplicação', field: 'app' },
    { title: 'Repositório', field: 'repoUrl' },
    { title: 'Branch', field: 'repoBranch', render: row => row.repoBranch || 'HEAD' },
    { title: 'Cron', field: 'schedule' },
    {
      title: 'Credencial',
      field: 'hasCredentials',
      render: row => (row.hasCredentials ? 'Secret por app' : 'Público'),
    },
    {
      title: 'Última execução',
      field: 'lastRunStartedAt',
      render: row =>
        row.lastRunStartedAt
          ? new Date(row.lastRunStartedAt).toLocaleString('pt-BR')
          : 'Ainda não executado',
    },
    {
      title: 'Último resultado',
      field: 'lastRunPhase',
      render: row =>
        row.lastRunPhase ? (
          <div>
            <span style={{ color: PHASE_COLOR[row.lastRunPhase] ?? 'inherit', fontWeight: 600 }}>
              {row.lastRunPhase}
            </span>
            {row.lastRunStep && (
              <Typography variant="caption" display="block" color="textSecondary">
                {row.lastRunStep}
                {row.lastRunStepMessage ? ` (${row.lastRunStepMessage})` : ''}
              </Typography>
            )}
            {row.lastRunFinishedAt && (
              <Typography variant="caption" display="block" color="textSecondary">
                {new Date(row.lastRunFinishedAt).toLocaleString('pt-BR')}
              </Typography>
            )}
          </div>
        ) : (
          'Sem execução'
        ),
    },
    {
      title: 'Ações',
      field: 'app',
      sorting: false,
      render: row => {
        const isRunning = !!row.lastRunPhase && RUNNING_PHASES.has(row.lastRunPhase);
        const isBusy = busyApp === row.app;
        return (
          <>
            <Tooltip title="Editar">
              <IconButton size="small" onClick={() => editPipeline(row)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={row.lastRunPhase ? 'Ver log' : 'Sem execução registrada ainda'}>
              <span>
                <IconButton
                  size="small"
                  disabled={!row.lastRunPhase}
                  onClick={() => openLog(row.app)}
                >
                  <DescriptionIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={isRunning ? 'Já há uma execução em andamento' : 'Disparar rebuild agora'}>
              <span>
                <IconButton
                  size="small"
                  disabled={isRunning || isBusy}
                  onClick={() => runAction(row.app, 'run')}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={isRunning ? 'Cancelar execução em andamento' : 'Nenhuma execução em andamento'}>
              <span>
                <IconButton
                  size="small"
                  disabled={!isRunning || isBusy}
                  onClick={() => runAction(row.app, 'cancel')}
                >
                  <StopIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </>
        );
      },
    },
  ];

  const requiredFieldsPresent =
    form.app &&
    form.repoUrl &&
    form.schedule &&
    (!form.privateRepo || form.gitToken || editingHasCredentials);

  return (
    <Page themeId="tool">
      <Header
        title="Pipelines"
        subtitle="Cadastre um repositório no pipeline genérico do Argo Workflows"
      />
      <Content>
        <Tabs
          value={activeTab}
          onChange={(_event, value) => setActiveTab(value)}
          indicatorColor="primary"
          textColor="primary"
          style={{ marginBottom: 24 }}
        >
          <Tab label="Cadastrar aplicação" />
          <Tab label={`Pipeline(s) cadastrado(s)${pipelines ? ` (${pipelines.length})` : ''}`} />
        </Tabs>

        <div hidden={activeTab !== 0}>
        <Paper style={{ padding: 24, marginBottom: 24 }}>
          <Typography variant="h5" gutterBottom>
            {pipelines?.some(pipeline => pipeline.app === form.app)
              ? `Editar ${form.app}`
              : 'Cadastrar aplicação'}
          </Typography>
          <Typography variant="body2" paragraph>
            O repositório já deve conter Dockerfile e k8s.yaml. A credencial privada é
            gravada diretamente em um Secret exclusivo no namespace argo; ela não é
            exibida novamente nem enviada como parâmetro do Workflow.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                label="Nome da aplicação"
                helperText="1-54 caracteres: minúsculas, números e hífen"
                value={form.app}
                onChange={event => setField('app', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                required
                label="URL HTTPS do repositório"
                placeholder="https://github.com/empresa/aplicacao.git"
                value={form.repoUrl}
                onChange={event => setField('repoUrl', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Branch"
                helperText="Vazio acompanha o HEAD padrão"
                value={form.repoBranch}
                onChange={event => setField('repoBranch', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Subdiretório"
                helperText="Vazio usa a raiz do repositório"
                value={form.repoSubdir}
                onChange={event => setField('repoSubdir', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                label="Agendamento cron"
                helperText="5 campos, por exemplo: */5 * * * *"
                value={form.schedule}
                onChange={event => setField('schedule', event.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.privateRepo}
                    onChange={event => setField('privateRepo', event.target.checked)}
                    color="primary"
                  />
                }
                label="Repositório privado"
              />
            </Grid>
            {form.privateRepo && (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Usuário Git"
                    helperText="GitHub: x-access-token; GitLab: oauth2"
                    value={form.gitUsername}
                    onChange={event => setField('gitUsername', event.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    type="password"
                    label={editingHasCredentials ? 'Novo token (opcional)' : 'Token'}
                    helperText={
                      editingHasCredentials
                        ? 'Deixe vazio para manter o Secret atual'
                        : 'Obrigatório para o primeiro cadastro privado'
                    }
                    value={form.gitToken}
                    onChange={event => setField('gitToken', event.target.value)}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.runNow}
                    onChange={event => setField('runNow', event.target.checked)}
                    color="primary"
                  />
                }
                label="Executar agora (além de salvar o agendamento)"
              />
            </Grid>
          </Grid>
          {errorMessage && <Typography color="error">{errorMessage}</Typography>}
          {successMessage && (
            <Typography style={{ color: '#15803d', marginTop: 8 }}>
              {successMessage}
            </Typography>
          )}
          <div style={{ marginTop: 16 }}>
            <Button
              variant="contained"
              color="primary"
              disabled={!requiredFieldsPresent || saving}
              onClick={savePipeline}
            >
              {saving ? 'Salvando…' : 'Salvar pipeline'}
            </Button>{' '}
            <Button disabled={saving} onClick={resetForm}>
              Limpar
            </Button>
          </div>
        </Paper>
        </div>

        <div hidden={activeTab !== 1}>
          {actionError && (
            <Typography color="error" style={{ marginBottom: 8 }}>
              {actionError}
            </Typography>
          )}
          {actionMessage && (
            <Typography style={{ color: '#15803d', marginBottom: 8 }}>
              {actionMessage}
            </Typography>
          )}
          {!pipelines && !errorMessage && <Progress />}
          {pipelines && (
            <Table
              title={`${pipelines.length} pipeline(s) cadastrado(s)`}
              columns={columns}
              data={pipelines}
              options={{ search: true, paging: pipelines.length > 20 }}
              actions={[
                {
                  icon: () => <RefreshIcon />,
                  tooltip: 'Atualizar lista agora',
                  isFreeAction: true,
                  onClick: () => loadPipelines(),
                },
              ]}
            />
          )}
        </div>
      </Content>

      <Dialog open={!!logDialogApp} onClose={() => setLogDialogApp(undefined)} maxWidth="md" fullWidth>
        <DialogTitle>Log de execução - {logDialogApp}</DialogTitle>
        <DialogContent>
          {logDialogLoading && <Progress />}
          {logDialogError && <Typography color="error">{logDialogError}</Typography>}
          {logDialogContent !== undefined && logDialogArchived && (
            <Typography variant="caption" color="textSecondary" component="div">
              Log arquivado no MinIO (o pod desta execução já foi removido; retenção de 30 dias).
            </Typography>
          )}
          {logDialogContent !== undefined && (
            <Typography
              component="pre"
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'monospace',
                fontSize: 12,
                maxHeight: '60vh',
                overflow: 'auto',
              }}
            >
              {logDialogContent || '(log vazio)'}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogDialogApp(undefined)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
};
